import { useEffect, useState } from "react";
import { Megaphone, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

// Static welcome/safety announcement shown on the second (reverse) ticker for AR/UR.
const STATIC_MSG = {
  ar: "قُد بأمان والتزم بأنظمة المرور — حافظ على عهدتك ومركبتك وسلامتك أولاً — نتمنى لكم يوماً موفقاً",
  ur: "احتیاط سے گاڑی چلائیں اور ٹریفک قوانین کی پابندی کریں — اپنی امانت اور گاڑی کا خیال رکھیں — سب کے لیے نیک خواہشات",
};

// A single scrolling bar. reverse=true moves it left→right (opposite of the default right→left).
const Bar = ({ msgs, reverse, gradient, icon: Icon, testid }) => {
  if (!msgs.length) return null;
  // Repeat the messages until one "unit" exceeds the viewport so two duplicated units loop seamlessly.
  const unit = [];
  let acc = "";
  while (acc.length < 140) {
    msgs.forEach((m) => unit.push(m));
    acc += msgs.join("   ") + "   ";
    if (acc.length === 0) break;
  }
  const duration = Math.max(20, Math.min(80, Math.round(acc.length * 0.35)));

  const Unit = ({ hidden }) => (
    <span className="inline-flex items-center" aria-hidden={hidden}>
      {unit.map((msg, i) => (
        <span key={i} className="inline-flex items-center text-sm font-semibold px-4 whitespace-nowrap">
          <span className="mx-2 h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />
          {msg}
        </span>
      ))}
    </span>
  );

  return (
    <div data-testid={testid} className={`${gradient} text-white shadow-md`}>
      <div className="mx-auto max-w-3xl flex items-stretch">
        <div className="flex items-center px-3 bg-black/15 shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div dir="ltr" className="wz-marquee-viewport relative flex-1 overflow-hidden py-2"
          style={{ "--wz-marquee-duration": `${duration}s` }}>
          <div className={`wz-marquee-track ${reverse ? "wz-marquee-reverse" : ""}`} dir="ltr">
            <Unit hidden={false} />
            <Unit hidden={true} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Announcement ticker(s) at the top of the employee portal.
export const AnnouncementTicker = () => {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [customMsg, setCustomMsg] = useState("");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const d = await api.portalAlerts();
        if (alive) { setItems(d.items || []); setCustomMsg(d.ticker_message || ""); }
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const alertMsgs = items.map((a) => a.message);
  const isRtlLang = lang === "ar" || lang === "ur";
  // Admin-editable message (company settings) overrides the built-in default.
  const staticMsg = (customMsg && customMsg.trim()) || STATIC_MSG[lang];
  // Second bar (AR/UR only): same admin alerts + the announcement, moving in the opposite direction.
  const secondMsgs = isRtlLang ? [...alertMsgs, staticMsg].filter(Boolean) : [];

  if (!alertMsgs.length && !secondMsgs.length) return null;

  return (
    <div data-testid="portal-alert-tickers">
      <Bar msgs={alertMsgs} reverse={false} icon={Megaphone} testid="portal-alert-ticker"
        gradient="bg-gradient-to-r from-red-600 via-orange-500 to-red-600" />
      {isRtlLang && (
        <Bar msgs={secondMsgs} reverse icon={Sparkles} testid="portal-alert-ticker-2"
          gradient="bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600" />
      )}
    </div>
  );
};
